import { useMemo, useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';
import type {
  AudiencePreviewResponse,
  CampaignResponse,
  Channel,
  CohortKind,
  Filter,
  FilterNode,
  RoutingPolicy,
  RunCohort,
  SegmentResponse,
} from '@outreach/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { RichTextEditor } from '@/features/campaigns/RichTextEditor';
import { useSegments } from '@/features/organization/useOrganization';
import {
  useCampaign,
  useCampaignAction,
  useCampaignReport,
  useCampaignRun,
  useCreateCampaign,
  useUpdateCampaign,
} from '@/features/campaigns/useCampaigns';
import { api, ApiRequestError } from '@/lib/api';

const FIELD = 'h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const CHANNELS: Channel[] = ['email', 'whatsapp'];
const ROUTINGS: RoutingPolicy[] = ['all_eligible', 'prefer_whatsapp'];

/** The campaign filter derived from a saved segment (the contract pins version 1). */
function segmentFilter(seg: SegmentResponse): Filter {
  return { version: 1, node: seg.filter as FilterNode };
}

function BackHeader({ title, subtitle }: { title: string; subtitle: string }): ReactElement {
  return (
    <div className="flex items-center gap-3">
      <Button asChild variant="ghost" size="icon" aria-label="Back to campaigns">
        <Link to="/campaigns">
          <ArrowLeft className="size-4" />
        </Link>
      </Button>
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}

// ─── New campaign ───────────────────────────────────────────────────────────────

function NewCampaign(): ReactElement {
  const navigate = useNavigate();
  const segments = useSegments();
  const create = useCreateCampaign();
  const [name, setName] = useState('');
  const [channels, setChannels] = useState<Channel[]>(['email']);
  const [routing, setRouting] = useState<RoutingPolicy>('all_eligible');
  const [segmentId, setSegmentId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [waKey, setWaKey] = useState(''); // "name|language"
  const [waBody, setWaBody] = useState('');
  const [waButtons, setWaButtons] = useState('');

  const wantsWhatsapp = channels.includes('whatsapp');
  const waTemplates = useQuery({
    queryKey: ['wa-templates'],
    queryFn: api.whatsapp.templates,
    enabled: wantsWhatsapp,
  });
  const waSync = useMutation({
    mutationFn: () => api.whatsapp.sync(),
    onSuccess: () => void waTemplates.refetch(),
  });
  const waList = waTemplates.data?.templates ?? [];

  const toggleChannel = (c: Channel) =>
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const canCreate = name.trim() !== '' && channels.length > 0;

  const csv = (s: string): string[] => s.split(',').map((v) => v.trim()).filter(Boolean);

  const submit = async () => {
    const seg = segments.data?.items.find((s) => s.id === segmentId);
    const content: Record<string, unknown> = { subject, html: body || '<p></p>' };
    if (wantsWhatsapp && waKey) {
      const [templateName, language] = waKey.split('|');
      content.whatsapp = { templateName, language, bodyParams: csv(waBody), buttonParams: csv(waButtons) };
    }
    try {
      const campaign = await create.mutateAsync({
        name: name.trim(),
        channels,
        routingPolicy: routing,
        filter: seg ? segmentFilter(seg) : undefined,
        // Body is authored as HTML/text; it is sanitized + personalized at render (M3-01).
        content,
      });
      toast.success('Draft created');
      navigate(`/campaigns/${campaign.id}`);
    } catch (e) {
      toast.error(e instanceof ApiRequestError ? e.message : 'Could not create campaign');
    }
  };

  return (
    <div className="space-y-6">
      <BackHeader title="New campaign" subtitle="Draft a campaign, then review before sending." />
      <Card className="max-w-2xl">
        <CardContent className="space-y-5 pt-6">
          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="October update" />
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Channels</legend>
            <div className="flex gap-4">
              {CHANNELS.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={channels.includes(c)}
                    onChange={() => toggleChannel(c)}
                    aria-label={c}
                  />
                  {c}
                </label>
              ))}
            </div>
          </fieldset>

          <div className="space-y-1.5">
            <Label htmlFor="routing">Routing</Label>
            <select id="routing" className={FIELD} value={routing} onChange={(e) => setRouting(e.target.value as RoutingPolicy)}>
              {ROUTINGS.map((r) => (
                <option key={r} value={r}>
                  {r.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="segment">Audience (saved segment)</Label>
            <select id="segment" className={FIELD} value={segmentId} onChange={(e) => setSegmentId(e.target.value)}>
              <option value="">All contacts</option>
              {segments.data?.items.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subject">Subject</Label>
            <Input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Hello from us" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="body">Body</Label>
            <RichTextEditor value={body} onChange={setBody} ariaLabel="Email body" />
            <p className="text-xs text-muted-foreground">
              Formatting here is how the email will look in the inbox. Use{' '}
              <code className="rounded bg-muted px-1">{'{{first_name}}'}</code> for
              personalization.
            </p>
          </div>

          {wantsWhatsapp && (
            <fieldset className="space-y-3 rounded-md border p-3">
              <legend className="px-1 text-sm font-medium">WhatsApp template</legend>
              {waList.length === 0 ? (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">No approved templates synced.</span>
                  <Button type="button" variant="outline" size="sm" onClick={() => waSync.mutate()} disabled={waSync.isPending}>
                    {waSync.isPending ? 'Syncing…' : 'Sync catalog'}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="wa-template">Template</Label>
                    <select id="wa-template" className={FIELD} value={waKey} onChange={(e) => setWaKey(e.target.value)}>
                      <option value="">Select a template…</option>
                      {waList
                        .filter((t) => t.status === 'approved')
                        .map((t) => (
                          <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
                            {t.name} · {t.language}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="wa-body">Body params (comma-separated)</Label>
                    <Input id="wa-body" value={waBody} onChange={(e) => setWaBody(e.target.value)} placeholder="{{first_name}}, …" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="wa-buttons">Button params (comma-separated)</Label>
                    <Input id="wa-buttons" value={waButtons} onChange={(e) => setWaButtons(e.target.value)} placeholder="deal-123" />
                  </div>
                </>
              )}
            </fieldset>
          )}

          <div className="flex justify-end">
            <Button onClick={submit} disabled={!canCreate || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create draft'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Draft review + launch ──────────────────────────────────────────────────────

function AudiencePanel({ preview }: { preview: AudiencePreviewResponse | undefined }): ReactElement {
  if (!preview) return <p className="text-sm text-muted-foreground">Preview the audience to see counts.</p>;
  return (
    <div className="space-y-2 text-sm">
      <Row label="Recipients (people)">
        <span className="tabular-nums">{preview.people}</span>
      </Row>
      <Row label="Messages">
        <span className="tabular-nums">{preview.messageCount}</span>
      </Row>
      {preview.perChannel.map((p) => (
        <Row key={p.channel} label={`Eligible · ${p.channel}`}>
          <span className="tabular-nums">{p.eligible}</span>
        </Row>
      ))}
      {preview.exclusions.length > 0 && (
        <div className="pt-1">
          <p className="mb-1 text-muted-foreground">Exclusions</p>
          <ul className="space-y-1">
            {preview.exclusions.map((x) => (
              <li key={x.reason} className="flex items-center justify-between">
                <span>{x.reason.replace(/_/g, ' ')}</span>
                <Badge variant="muted">{x.count}</Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): ReactElement {
  return (
    <div className="flex items-center justify-between gap-4 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  );
}

function DraftReview({ campaign }: { campaign: CampaignResponse }): ReactElement {
  const actions = useCampaignAction(campaign.id);
  const update = useUpdateCampaign(campaign.id);
  const [mode, setMode] = useState<'send' | 'schedule'>('send');
  const [localDateTime, setLocalDateTime] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const filter = campaign.filter as Filter | null;

  // Editable email content, prefilled from the persisted draft so a reload shows
  // exactly what was saved (UAT-081). WhatsApp content is template-based elsewhere.
  const savedContent = (campaign.content ?? {}) as { subject?: string; html?: string };
  const hasEmail = campaign.channels.includes('email');
  const [subject, setSubject] = useState(savedContent.subject ?? '');
  const [body, setBody] = useState(savedContent.html ?? '');

  const saveContent = async () => {
    try {
      await update.mutateAsync({ content: { ...(campaign.content ?? {}), subject, html: body } });
      toast.success('Content saved');
    } catch (e) {
      toast.error(e instanceof ApiRequestError ? e.message : 'Could not save content');
    }
  };

  // Preview is scoped to the campaign's channels so counts match what will send.
  const preview = useMutation({
    mutationFn: () =>
      api.audience.preview({
        filter: filter ?? { version: 1, node: { type: 'all', nodes: [] } },
        channels: campaign.channels,
      }),
  });
  const runPreview = () => preview.mutate();

  const launch = async () => {
    try {
      await actions.launch.mutateAsync({
        route: mode,
        expectedRevision: campaign.revision,
        idempotencyKey: crypto.randomUUID(),
        ...(mode === 'schedule' ? { schedule: { localDateTime, timezone } } : {}),
      });
      toast.success(mode === 'schedule' ? 'Campaign scheduled' : 'Campaign sending');
    } catch (e) {
      if (e instanceof ApiRequestError && e.fieldErrors?.reason?.[0] === 'stale_revision') {
        toast.error('This campaign changed since you opened it. Reload the review.');
      } else {
        toast.error(e instanceof ApiRequestError ? e.message : 'Launch failed');
      }
    }
  };

  return (
    <div className="space-y-6">
      <BackHeader title={campaign.name} subtitle={`Draft · rev ${campaign.revision} · review before sending.`} />
      {hasEmail && (
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base">Email content</CardTitle>
            <span className="text-xs text-muted-foreground">Saved at revision {campaign.revision}</span>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="draft-subject">Subject</Label>
              <Input id="draft-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="draft-body">Body</Label>
              <RichTextEditor value={body} onChange={setBody} ariaLabel="Email body" />
            </div>
            <Button variant="outline" size="sm" onClick={saveContent} disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save content'}
            </Button>
          </CardContent>
        </Card>
      )}
      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base">Audience</CardTitle>
            <Button variant="outline" size="sm" onClick={runPreview} disabled={preview.isPending}>
              {preview.isPending ? 'Previewing…' : 'Preview'}
            </Button>
          </CardHeader>
          <CardContent>
            <AudiencePanel preview={preview.data} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Send</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Row label="Channels">{campaign.channels.join(', ')}</Row>
            <Row label="Routing">
              <Badge variant="secondary">{campaign.routingPolicy.replace(/_/g, ' ')}</Badge>
            </Row>
            <Separator />
            <div className="flex gap-3 text-sm">
              <label className="flex items-center gap-2">
                <input type="radio" name="mode" checked={mode === 'send'} onChange={() => setMode('send')} /> Send now
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="mode" checked={mode === 'schedule'} onChange={() => setMode('schedule')} /> Schedule
              </label>
            </div>
            {mode === 'schedule' && (
              <div className="space-y-2">
                <input
                  aria-label="Schedule time"
                  type="datetime-local"
                  className={FIELD}
                  value={localDateTime}
                  onChange={(e) => setLocalDateTime(e.target.value)}
                />
                <Input aria-label="Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
              </div>
            )}
            <Button className="w-full" onClick={launch} disabled={actions.launch.isPending || (mode === 'schedule' && !localDateTime)}>
              <Send className="size-4" /> {mode === 'schedule' ? 'Schedule campaign' : 'Send campaign'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Launched run report + lifecycle ─────────────────────────────────────────────

function RunReport({ campaign }: { campaign: CampaignResponse }): ReactElement {
  const active = ['scheduled', 'sending'].includes(campaign.status);
  const run = useCampaignRun(campaign.id, active);
  const report = useCampaignReport(campaign.id, active);
  const actions = useCampaignAction(campaign.id);
  const counts = run.data?.counts;
  const eng = report.data?.engagement;
  const disp = report.data?.dispatch;

  // Re-engagement cohorts (no observed open/click) are loaded on demand (UAT-155/156).
  const [cohort, setCohort] = useState<RunCohort | null>(null);
  const loadCohort = useMutation({
    mutationFn: (kind: CohortKind) => api.campaigns.cohort(campaign.id, kind),
    onSuccess: (data) => setCohort(data),
    onError: (e) => toast.error(e instanceof ApiRequestError ? e.message : 'Could not load cohort'),
  });

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
    } catch (e) {
      toast.error(e instanceof ApiRequestError ? e.message : 'Action failed');
    }
  };

  return (
    <div className="space-y-6">
      <BackHeader title={campaign.name} subtitle="Run report" />
      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base">Delivery</CardTitle>
            <Badge variant={campaign.status === 'completed' ? 'success' : 'secondary'}>
              {campaign.status.replace(/_/g, ' ')}
            </Badge>
          </CardHeader>
          <CardContent>
            {counts ? (
              <div className="space-y-1">
                <Row label="Recipients">
                  <span className="tabular-nums">{counts.recipients}</span>
                </Row>
                <Row label="Messages">
                  <span className="tabular-nums">{counts.messages}</span>
                </Row>
                <Row label="Excluded">
                  <span className="tabular-nums">{counts.excluded}</span>
                </Row>
                {run.data?.scheduledAt && <Row label="Scheduled">{new Date(run.data.scheduledAt).toLocaleString()}</Row>}
                {counts.exclusionReasons.length > 0 && (
                  <div className="pt-2">
                    <p className="mb-1 text-sm text-muted-foreground">Exclusions</p>
                    <ul className="space-y-1 text-sm">
                      {counts.exclusionReasons.map((x) => (
                        <li key={x.reason} className="flex items-center justify-between">
                          <span>{x.reason.replace(/_/g, ' ')}</span>
                          <Badge variant="muted">{x.count}</Badge>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Loading run…</p>
            )}

            {disp && (
              <div className="mt-3 border-t pt-2">
                <p className="mb-1 text-sm text-muted-foreground">Dispatch</p>
                <Row label="Accepted">
                  <span className="tabular-nums">{disp.accepted}</span>
                </Row>
                {disp.inFlight > 0 && (
                  <Row label="In flight">
                    <span className="tabular-nums">{disp.inFlight}</span>
                  </Row>
                )}
                {disp.failed > 0 && (
                  <Row label="Failed">
                    <span className="tabular-nums">{disp.failed}</span>
                  </Row>
                )}
                {disp.unknown > 0 && (
                  <Row label="Unknown (reconcile)">
                    <span className="tabular-nums">{disp.unknown}</span>
                  </Row>
                )}
              </div>
            )}
            {eng && (
              <div className="mt-3 border-t pt-2">
                <p className="mb-1 text-sm text-muted-foreground">Engagement</p>
                <Row label="Delivered">
                  <span className="tabular-nums">{eng.delivered}</span>
                </Row>
                <Row label="Unique opens">
                  <span className="tabular-nums">{eng.uniqueOpens}</span>
                </Row>
                <Row label="Unique clicks">
                  <span className="tabular-nums">{eng.uniqueClicks}</span>
                </Row>
                {eng.reads > 0 && (
                  <Row label="WhatsApp reads">
                    <span className="tabular-nums">{eng.reads}</span>
                  </Row>
                )}
                {eng.bounced > 0 && (
                  <Row label="Bounced">
                    <span className="tabular-nums">{eng.bounced}</span>
                  </Row>
                )}
                {eng.complained > 0 && (
                  <Row label="Complaints">
                    <span className="tabular-nums">{eng.complained}</span>
                  </Row>
                )}
                {eng.observedWithoutDelivery > 0 && (
                  <Row label="Observed (delivery unconfirmed)">
                    <span className="tabular-nums">{eng.observedWithoutDelivery}</span>
                  </Row>
                )}
              </div>
            )}
            {report.data && report.data.byChannel.length > 1 && (
              <div className="mt-3 border-t pt-2">
                <p className="mb-1 text-sm text-muted-foreground">By channel</p>
                <div className="space-y-2">
                  {report.data.byChannel.map((c) => (
                    <div key={c.channel} className="rounded-md bg-muted/50 p-2 text-sm">
                      <p className="mb-1 font-medium capitalize">{c.channel}</p>
                      <Row label="Accepted">
                        <span className="tabular-nums">{c.dispatch.accepted}</span>
                      </Row>
                      <Row label="Delivered">
                        <span className="tabular-nums">{c.engagement.delivered}</span>
                      </Row>
                      {/* Email opens and WhatsApp reads are reported per channel — never funnelled together. */}
                      {c.channel === 'whatsapp' ? (
                        <Row label="Reads">
                          <span className="tabular-nums">{c.engagement.reads}</span>
                        </Row>
                      ) : (
                        <Row label="Unique opens">
                          <span className="tabular-nums">{c.engagement.uniqueOpens}</span>
                        </Row>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {['scheduled', 'sending'].includes(campaign.status) && (
              <Button variant="outline" onClick={() => act(() => actions.pause.mutateAsync(), 'Paused')}>
                Pause
              </Button>
            )}
            {campaign.status === 'paused' && (
              <Button variant="outline" onClick={() => act(() => actions.resume.mutateAsync(), 'Resumed')}>
                Resume
              </Button>
            )}
            {['scheduled', 'sending', 'paused'].includes(campaign.status) && (
              <Button variant="outline" onClick={() => act(() => actions.cancel.mutateAsync(), 'Cancelled')}>
                Cancel
              </Button>
            )}
            <Button variant="outline" onClick={() => act(() => actions.clone.mutateAsync(), 'Cloned to a new draft')}>
              Clone
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">Re-engagement cohorts</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => loadCohort.mutate('no_open')} disabled={loadCohort.isPending}>
              No opens
            </Button>
            <Button variant="outline" size="sm" onClick={() => loadCohort.mutate('no_click')} disabled={loadCohort.isPending}>
              No clicks
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!cohort && (
            <p className="text-sm text-muted-foreground">
              Load contacts delivered at least 24h ago with no observed open or click, for a follow-up.
            </p>
          )}
          {cohort && (
            <div className="space-y-2 text-sm">
              <p className="text-muted-foreground">
                {cohort.members.length} recipient(s) with{' '}
                {cohort.kind === 'no_click' ? 'no observed click' : 'no observed open'} · delivered on or before{' '}
                {new Date(cohort.cutoff).toLocaleString()}
              </p>
              <a className="text-primary underline" href={api.campaigns.cohortCsvUrl(campaign.id, cohort.kind)} download>
                Export CSV
              </a>
              {cohort.members.length > 0 && (
                <ul className="max-h-40 overflow-y-auto rounded-md border p-2">
                  {cohort.members.slice(0, 50).map((m) => (
                    <li key={m.contactId} className="truncate">
                      {m.endpoint}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Router ──────────────────────────────────────────────────────────────────────

export function ComposerReviewPage(): ReactElement {
  const { id } = useParams();
  const campaign = useCampaign(id);
  const isDraft = useMemo(() => campaign.data?.status === 'draft', [campaign.data?.status]);

  if (!id) return <NewCampaign />;
  if (campaign.isLoading || !campaign.data) {
    return <p className="text-sm text-muted-foreground">Loading campaign…</p>;
  }
  return isDraft ? <DraftReview campaign={campaign.data} /> : <RunReport campaign={campaign.data} />;
}
